import React, { useState } from 'react';
import { Upload, FileText, Users, Building2, PlusCircle, Download, AlertCircle, CheckCircle } from 'lucide-react';

const ImportPage = ({ selectedCompany, addError }) => {
    const [activeImportType, setActiveImportType] = useState('shareholders');
    const [dragActive, setDragActive] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [importStatus, setImportStatus] = useState(null);

    const importTypes = [
        {
            id: 'shareholders',
            name: 'Shareholders',
            icon: Users,
            description: 'Import shareholder information from CSV',
            sampleColumns: ['name', 'email', 'entity_type', 'type'],
            color: 'blue'
        },
        {
            id: 'directors',
            name: 'Directors',
            icon: Building2,
            description: 'Import directors and company secretaries',
            sampleColumns: ['first_name', 'last_name', 'email', 'is_director', 'date_appointed'],
            color: 'green'
        },
        {
            id: 'issuances',
            name: 'Share Issuances',
            icon: PlusCircle,
            description: 'Import share issuances and transactions',
            sampleColumns: ['shareholder_name', 'share_class', 'shares', 'price_per_share', 'issue_date'],
            color: 'purple'
        },
        {
            id: 'companies',
            name: 'Companies',
            icon: FileText,
            description: 'Import multiple companies at once',
            sampleColumns: ['name', 'description', 'address', 'incorporation_date'],
            color: 'orange'
        }
    ];

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = (file) => {
        // Validate file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
            addError('Please upload a CSV file');
            return;
        }

        setUploadedFile(file);
        setImportStatus('uploaded');
        addError(`File "${file.name}" uploaded successfully. Ready for import.`);
    };

    const handleImport = () => {
        if (!uploadedFile) {
            addError('Please upload a file first');
            return;
        }

        if (!selectedCompany) {
            addError('Please select a company first');
            return;
        }

        // Simulate import process
        setImportStatus('importing');
        
        setTimeout(() => {
            setImportStatus('success');
            addError(`${activeImportType} imported successfully!`);
        }, 2000);
    };

    const downloadTemplate = (type) => {
        const templates = {
            shareholders: 'name,email,entity_type,type\nJohn Doe,john@example.com,Individual,Founder\nAcme Corp,contact@acme.com,Company,Investor',
            directors: 'first_name,last_name,email,is_director,date_appointed\nJane,Smith,jane@example.com,true,2024-01-01\nBob,Johnson,bob@example.com,true,2024-01-15',
            issuances: 'shareholder_name,share_class,shares,price_per_share,issue_date\nJohn Doe,Common,100000,0.01,2024-01-01\nAcme Corp,Preference,50000,1.00,2024-03-01',
            companies: 'name,description,address\nMy Startup,Tech company,123 Main St\nAnother Co,Service business,456 Oak Ave'
        };

        const csvContent = templates[type];
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_template.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getCurrentImportType = () => {
        return importTypes.find(type => type.id === activeImportType);
    };

    const renderUploadArea = () => {
        const currentType = getCurrentImportType();
        
        return (
            <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                        ? 'border-blue-500 bg-blue-50' 
                        : uploadedFile 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-gray-300 bg-gray-50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                {uploadedFile ? (
                    <div className="space-y-4">
                        <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                        <div>
                            <p className="text-lg font-medium text-green-700">File Ready for Import</p>
                            <p className="text-sm text-green-600">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500 mt-2">
                                {(uploadedFile.size / 1024).toFixed(1)} KB
                            </p>
                        </div>
                        <button
                            onClick={() => {
                                setUploadedFile(null);
                                setImportStatus(null);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800"
                        >
                            Choose Different File
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <Upload className={`h-12 w-12 mx-auto ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
                        <div>
                            <p className="text-lg font-medium text-gray-700">
                                Drop your {currentType.name} CSV file here
                            </p>
                            <p className="text-sm text-gray-500">or click to browse</p>
                        </div>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileSelect}
                            className="hidden"
                            id="file-upload"
                        />
                        <label
                            htmlFor="file-upload"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            Choose File
                        </label>
                    </div>
                )}
            </div>
        );
    };

    if (!selectedCompany) {
        return (
            <div className="space-y-6">
                <h2 className="text-2xl font-bold">Import Data</h2>
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    Please select a company to import data.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Import Data</h2>
                    <p className="text-gray-600">Import data from CSV files for {selectedCompany.name}</p>
                </div>
                <span className="text-sm text-gray-500">Company: {selectedCompany.name}</span>
            </div>

            {/* Import Type Selection */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Import Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {importTypes.map((type) => (
                        <button
                            key={type.id}
                            onClick={() => {
                                setActiveImportType(type.id);
                                setUploadedFile(null);
                                setImportStatus(null);
                            }}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                                activeImportType === type.id
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <type.icon className={`h-6 w-6 mb-2 ${
                                activeImportType === type.id ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <h4 className="font-medium text-gray-900">{type.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Upload Area */}
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Upload {getCurrentImportType().name} Data
                    </h3>
                    <button
                        onClick={() => downloadTemplate(activeImportType)}
                        className="inline-flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download Template
                    </button>
                </div>

                {renderUploadArea()}

                {/* Sample Format */}
                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Expected CSV Format:</h4>
                    <div className="text-sm text-gray-600">
                        <code className="bg-white px-2 py-1 rounded border">
                            {getCurrentImportType().sampleColumns.join(', ')}
                        </code>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Download the template above for the correct format and example data.
                    </p>
                </div>
            </div>

            {/* Import Actions */}
            {uploadedFile && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Options</h3>
                    
                    <div className="space-y-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="skip-duplicates"
                                defaultChecked
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="skip-duplicates" className="ml-3 text-sm text-gray-700">
                                Skip duplicate entries (recommended)
                            </label>
                        </div>
                        
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="validate-data"
                                defaultChecked
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="validate-data" className="ml-3 text-sm text-gray-700">
                                Validate data before import
                            </label>
                        </div>
                        
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="send-notifications"
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <label htmlFor="send-notifications" className="ml-3 text-sm text-gray-700">
                                Send email notifications to imported shareholders
                            </label>
                        </div>
                    </div>

                    <div className="mt-6 flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-600">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            This action will add data to your existing records
                        </div>
                        
                        <button
                            onClick={handleImport}
                            disabled={importStatus === 'importing'}
                            className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {importStatus === 'importing' ? (
                                <>
                                    <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                                    Importing...
                                </>
                            ) : (
                                <>
                                    <Upload className="h-4 w-4 mr-2" />
                                    Start Import
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Import Status */}
            {importStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-3" />
                        <div>
                            <h3 className="text-sm font-medium text-green-800">Import Completed Successfully</h3>
                            <p className="text-sm text-green-700 mt-1">
                                Your {getCurrentImportType().name.toLowerCase()} data has been imported to {selectedCompany.name}.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Guidelines */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Guidelines</h3>
                <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p>CSV files should use comma separation and include a header row</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p>Date formats should be YYYY-MM-DD (e.g., 2024-01-15)</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p>Boolean fields should use 'true' or 'false' values</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p>Email addresses will be validated during import</p>
                    </div>
                    <div className="flex items-start">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <p>Maximum file size: 10MB (approximately 50,000 rows)</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportPage;